import type { PartialDeep, UndefinedOnPartialDeep } from 'type-fest';

import type { AssetPreview } from '@repo/api-types/customer-assets';
import type { DtoSaving } from '@repo/api-types/order-retrieval';
import { isObject } from '@repo/utils/type.utils';

import type { DraftAsset, DraftAssetDetails, SateliteSalesLocation, SelfServeSalesLocations } from './draft.types';
import type { OrderAsset } from './order.types';
import type { QuoteAsset, QuoteAssetDetails } from './quotation.types';

export const assetTypes = {
    order: 'order',
    draft: 'draft',
    quote: 'quote',
} as const;

export const assetStatusTypes = {
    CANCELLED: 'CANCELLED',
    CANCELED: 'CANCELED', // TODO: extra status ussed only for quotations a temporary solution until the BE API is updated
    CONVERTED: 'CONVERTED',
    COMPLETED: 'COMPLETED',
    EXPIRED: 'EXPIRED',
} as const;

type AssetStatusType = (typeof assetStatusTypes)[keyof typeof assetStatusTypes];

export const isValidAssetStatusType = (status: unknown): status is AssetStatusType => {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return typeof status === 'string' && Object.values(assetStatusTypes).includes(status as AssetStatusType);
};

export type AssetType = (typeof assetTypes)[keyof typeof assetTypes];

export type AssetDataType = OrderAsset | QuoteAsset | DraftAsset;

export const isAssetType = (value: unknown): value is AssetType => {
    // oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
    return typeof value === 'string' && Object.values(assetTypes).includes(value as AssetType);
};

export const isAssetsResponse = (value: unknown): value is GetAssetsResponse => {
    return isObject(value) && 'assets' in value && 'errors' in value;
};

export interface GetAssetsResponse {
    assets: AssetPreview[];
    errors: AssetError[];
}

export interface AssetDrawing {
    drawingUrlType?: string | undefined;
    drawingUrl?: string | undefined;
    drawingIdSource?: string | undefined;
    drawingId?: string | undefined;
    imageUrl?: string | undefined;
    drawingName?: string | undefined;
}

export type MeasurementSystem = 'metric' | 'imperial';

export interface PackageMeasurementValue {
    unit: string;
    value: number | null;
}

/**
 * A single package dimension as returned by the item APIs. Every entry carries both
 * measurement systems; `consumerPackNumber` identifies the package an entry belongs
 * to, so an article spanning several packages produces one entry per package.
 */
export interface PackageMeasurement {
    type: string;
    metric?: PackageMeasurementValue;
    imperial?: PackageMeasurementValue;
    consumerPackNumber?: number;
}

/** A measurement resolved to the configured measurement system, ready to render. */
export interface ArticleMeasurement {
    value: number;
    unit: string;
}

// TODO: Change these with the real types coming from the API
export interface AssetArticle {
    type?: 'ARTICLE' | 'PRODUCT' | 'GROUP' | 'TEXTLINE';
    seqNo?: number | undefined;
    referenceId?: number | undefined;
    name?: string | undefined;
    itemId?: string | undefined;
    groupNo?: number | undefined;
    imageUrl?: string | undefined;
    description?: string | undefined;
    isReferenceItem?: boolean | undefined;
    isSelfServe?: boolean | undefined;
    quantity?: number | undefined;
    unitPrice?: number | undefined;
    totalPrice?: number | undefined;
    unitPriceIncludingTax?: number | undefined;
    unitPriceExcludingTax?: number | undefined;
    totalPriceIncludingTax?: number | undefined;
    totalPriceExcludingTax?: number | undefined;
    numberOfPackages?: number | undefined;
    /** Total package weight for one unit of the article, in the configured measurement system. */
    packageWeight?: ArticleMeasurement | undefined;
    /** Total package volume for one unit of the article, in the configured measurement system. */
    packageVolume?: ArticleMeasurement | undefined;
    selfServeSalesLocations?: UndefinedOnPartialDeep<Partial<SelfServeSalesLocations>> | undefined;
    serveSalesLocations?: UndefinedOnPartialDeep<Partial<SateliteSalesLocation>> | undefined;
    currency?: string | undefined;
    childItems?: AssetArticle[] | undefined;
    savings?: DtoSaving[];
}

export type CustomerType = 'Family' | 'Private' | 'Business';

export interface AssetError {
    assetType: AssetType;
    error: {
        errorCode: string;
        errorMessage: string;
    };
}

export type PartialDraftAssetDetails = PartialDeep<
    DraftAssetDetails,
    { recurseIntoArrays: true; allowUndefinedInNonTupleArrays: true }
>;

export type PartialQuoteAssetDetails = PartialDeep<
    QuoteAssetDetails,
    { recurseIntoArrays: true; allowUndefinedInNonTupleArrays: true }
>;
